import { Cookie } from '@/Cookie';
import axios, { AxiosRequestConfig, Method } from 'axios';
import { showAxiosErrorMessage } from './utils';

export abstract class AjaxHelper {
  /**
   * Method that will call a get call
   * 
   * @param url The url that we want to call the get on
   * @param config The extra configurations that will go along with the ajax request
   */
  public static async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request('get', url, undefined, config);
  }

  /**
   * Method that will call a post call
   * 
   * @param url The url that we want to call the post on
   * @param data The data that should go along with the request
   * @param config The extra configurations that will go along with the ajax request
   */
  public static async post<T, K>(url: string, data?: K, config?: AxiosRequestConfig): Promise<T> {
    return this.request('post', url, data, config);
  }

  /**
   * Method that will call a put ajax call
   * 
   * @param url The url that we want to call the put on
   * @param data The data that should go along with the request
   * @param config The extra configurations that will go along with the ajax request
   */
  public static async put<T, K>(url: string, data?: K, config?: AxiosRequestConfig): Promise<T> {
    return this.request('put', url, data, config);
  }

  /**
   * Method that will call a delete ajax call
   * 
   * @param url The url we want to call delete on
   * @param config The extra configurations that will go along with the ajax request
   */
  public static async delete<T, K>(url: string, data?: K, config?: AxiosRequestConfig): Promise<T> {
    return this.request('delete', url, data, config);
  }

  /**
   * Helper method to generate a standard request that will be used everywhere in the system
   * 
   * @param method The method we are processing
   * @param url The url where we want to make the ajax request
   * @param data The data that should go along with the request
   * @param config The special configurations that should be included with the request
   */
  private static async request<T>(method: Method, url: string, data?: T, config?: AxiosRequestConfig) {
    config = config ?? {};

    // Add in the auth tokens
    const auth = Cookie.get('ApiSession');
    if (auth !== undefined) {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = `bearer ${auth}`;
    }

    // Set the method, url and data to send off with the request
    config.method = method;
    config.url = this.join(process.env.VUE_APP_API_URI, url);
    if (data !== undefined) {
      config.data = data;
    }
    return (await axios.request(config)).data;
  }

  /**
   * Helper method to join url fragments together
   * 
   * @param urlFragments The url fragments that need to be joined together no mater if the end or being with a /
   */
  private static join(...urlFragments: (string | undefined)[]) {
    urlFragments = urlFragments
      .filter(x => x !== undefined && x.length > 0)
      .map((x, i) => {
        if (i === 0) return x?.replace(/\/$/, '');
        return x?.replace(/^\//, '').replace(/\/$/, '');
      });
    return urlFragments.join('/');
  }
}